import React, { useState } from 'react';
import DropdownMenuButton from '@components/EntitiesDropdownMenu/DropdownMenuButton';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as SuccessSvg } from '@assets/icons/success.svg';
import { Dropdown, message } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { ReactComponent as ApprovalUsersIconPanelSvg } from '@assets/icons/approval-users-icon-panel.svg';
import { ReactComponent as ApprovalRoutingSvg } from '@assets/icons/approval-routing.svg';
import { ReactComponent as DeleteSvg } from '@assets/icons/delete.svg';
import { ReactComponent as HeartSvg } from '@assets/icons/heart.svg';
import { ReactComponent as SendSvg } from '@assets/icons/send.svg';

import { ReactComponent as ProjectInfoSvg } from '@assets/icons/dropdown/project-info.svg';
import { ReactComponent as RenameSvg } from '@assets/icons/dropdown/rename.svg';
import { ReactComponent as DuplicateSvg } from '@assets/icons/dropdown/duplicate.svg';
import { ReactComponent as UserPlusSvg } from '@assets/icons/dropdown/user-plus.svg';
import { ReactComponent as LinkSvg } from '@assets/icons/dropdown/link.svg';
import { ReactComponent as LinkPlusSvg } from '@assets/icons/dropdown/link-plus.svg';

import './EntitiesDropdownMenu.less';

import { CampaignItemDto } from '@api/Api';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { ActionToast } from '@components/Toasts';
import {
  restoreRemovedCampaign,
  setFavoriteCampaignAction
} from '@redux/actions/campaignAction';
import {
  toggleEntityDeletionModal,
  toggleCampaignInfoModal,
  toggleCampaignInvitationModal,
  toggleRenameEntityModal,
  toggleBillingModal,
  toggleApprovalConflictAssetsModal,
  toggleDuplicateProjectModal,
  toggleShareAssetModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import classNames from 'classnames';

type CampaignDropDownMenuProps = {
  campaign: CampaignItemDto;
  onDeleteCallback?: () => void;
  placement?: 'bottomRight' | 'bottomLeft' | 'topRight' | 'topLeft';
  overlayClassName?: string;
  triggerClassName?: string;
};

function CampaignDropDownMenu({
  campaign,
  onDeleteCallback,
  placement = 'bottomRight',
  overlayClassName,
  triggerClassName
}: CampaignDropDownMenuProps) {
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false);
  const isDeleted = !!campaign.deletedAt;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const [currentWorkspace] = useCurrentWorkspace();

  const canUseBatchApprovals =
    !!currentOrganization.entity?.features.batchApprovals;
  const awFeatureEnabled =
    !!currentOrganization.entity?.features.approvalRouting;
  const hasExternalContributors =
    !!currentOrganization.entity?.features.externalContributors;

  const deletedItems: ItemType[] = [
    ...(campaign.permissions.delete
      ? [
          {
            key: 'delete_forever',
            label: 'Delete forever',
            onClick: () => {
              dispatch(
                toggleEntityDeletionModal({
                  entity: campaign,
                  page: 'campaign',
                  permanent: true,
                  visible: true
                })
              );
            }
          }
        ]
      : []),
    ...(campaign.permissions.delete
      ? [
          {
            key: 'restore',
            label: 'Restore',
            onClick: () => {
              dispatch(restoreRemovedCampaign({ campaignId: campaign.id }));
            }
          }
        ]
      : [])
  ];

  const activeItems: ItemType[] = [
    {
      key: 'campaign_info',
      label: 'Project info',
      icon: <ProjectInfoSvg />,
      onClick: () => {
        dispatch(
          toggleCampaignInfoModal({
            activeTab: 'details',
            campaign,
            visible: true
          })
        );
      }
    },
    ...(campaign.permissions.changeDetails
      ? [
          {
            key: 'rename',
            label: 'Rename',
            icon: <RenameSvg />,
            onClick: () => {
              dispatch(
                toggleRenameEntityModal({
                  entity: campaign,
                  page: 'campaign',
                  visible: true
                })
              );
            }
          }
        ]
      : []),
    ...(currentWorkspace.permissions.createCampaigns
      ? [
          {
            key: 'duplicate',
            label: 'Duplicate',
            icon: <DuplicateSvg />,
            onClick: () => {
              dispatch(
                toggleDuplicateProjectModal({
                  visible: true,
                  campaignId: campaign.id,
                  workspaceId: campaign.workspaceId,
                  isPrivate: campaign.isPrivate,
                  name: `${campaign.name} copy`
                })
              );
            }
          }
        ]
      : []),
    {
      key: 'toggle_favorite',
      icon: (
        <HeartSvg
          className={classNames({ 'b-favorite': campaign.isFavorite })}
        />
      ),
      label: !campaign.isFavorite ? 'Add to favorite' : 'Remove from favorite',
      onClick: () => {
        if (!campaign) return;
        dispatch(
          setFavoriteCampaignAction({
            campaignId: campaign.id || '',
            isFavorite: !campaign.isFavorite
          })
        );
        if (campaign.isFavorite) {
          toast(
            <ActionToast
              title="Project has been removed from favorites"
              onUndo={undefined}
              description={undefined}
              closeToast={undefined}
            />,
            {
              hideProgressBar: true,
              bodyClassName: 'toast_container--invitation'
            }
          );
        }
      }
    },
    {
      key: 'create_approval_routing',
      icon: <ApprovalRoutingSvg className="b-opacity-icon" />,
      label: (
        <UpgradePlanTooltip
          text={
            !canUseBatchApprovals || !awFeatureEnabled
              ? ' to an Enterprise plan to route multiple media assets for approval at one time.'
              : 'Click to route all media in project and folders for approval'
          }
          isCanVisible={true}
          placement="right"
          isShowUpgradeText={!canUseBatchApprovals || !awFeatureEnabled}
          innerStyle={{
            transform: 'translate(-30px, 0)',
            maxWidth: '300px'
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '4px',
              alignItems: 'center'
            }}
          >
            Create approval routing
            {(!canUseBatchApprovals || !awFeatureEnabled) && (
              <UpgradePlanSvg className="upgrade-icon" />
            )}
          </div>
        </UpgradePlanTooltip>
      ),
      onClick: () => {
        if (!canUseBatchApprovals || !awFeatureEnabled) {
          dispatch(toggleBillingModal({ visible: true }));
          return;
        }
        dispatch(
          toggleApprovalConflictAssetsModal({
            visible: true,
            title: 'Route project for approval',
            campaignId: campaign.id,
            workspaceId: campaign.workspaceId,
            callback: (aw) => {
              if (aw) {
                navigate(
                  `/campaigns/approvals/${aw.workspaceId}/${aw.campaignId}/${aw.id}`
                );
                return;
              }
              navigate(`/campaigns/single/${campaign.id}?tab=approvals`);
            }
          })
        );
      }
    },
    ...(campaign.permissions.inviteContributors
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'add_contributor',
            icon: <UserPlusSvg />,
            label: (
              <UpgradePlanTooltip
                text=" to a paid plan to add external contributors."
                isCanVisible={!hasExternalContributors}
                placement="right"
                innerStyle={{
                  transform: 'translate(-30px, 0)'
                }}
              >
                <div
                  style={
                    !hasExternalContributors
                      ? {
                          display: 'flex',
                          alignItems: 'center'
                        }
                      : undefined
                  }
                >
                  Add сontributor{' '}
                  {!hasExternalContributors && (
                    <UpgradePlanSvg style={{ marginLeft: 4 }} />
                  )}
                </div>
              </UpgradePlanTooltip>
            ),
            onClick: () => {
              if (!hasExternalContributors) {
                dispatch(toggleBillingModal({ visible: true }));
                return;
              }
              dispatch(
                toggleCampaignInvitationModal({
                  visible: true,
                  campaign
                })
              );
            }
          }
        ]
      : []),
    ...(campaign?.permissions.changeAdditionalPermissions
      ? [
          ...(!campaign.permissions.inviteContributors
            ? [
                {
                  type: 'divider' as const
                }
              ]
            : []),
          {
            key: 'contributor_permissions',
            icon: <ApprovalUsersIconPanelSvg />,
            label: (
              <UpgradePlanTooltip
                text=" to a paid plan to manage contributor permissions."
                isCanVisible={!hasExternalContributors}
                placement="right"
                innerStyle={{
                  transform: 'translate(-30px, 0)'
                }}
              >
                <div
                  style={
                    !hasExternalContributors
                      ? {
                          display: 'flex',
                          alignItems: 'center'
                        }
                      : undefined
                  }
                >
                  Сontributor permissions{' '}
                  {!hasExternalContributors && (
                    <UpgradePlanSvg style={{ marginLeft: 4 }} />
                  )}
                </div>
              </UpgradePlanTooltip>
            ),
            onClick: () => {
              if (!hasExternalContributors) {
                dispatch(toggleBillingModal({ visible: true }));
                return;
              }
              dispatch(
                toggleCampaignInfoModal({
                  activeTab: 'settings',
                  campaign,
                  visible: true
                })
              );
            }
          }
        ]
      : []),
    {
      type: 'divider' as const
    },
    {
      key: 'share_project_link',
      label: 'Copy project link',
      icon: <LinkSvg />,
      onClick: () => {
        navigator.clipboard.writeText(
          `${window.location.origin}/campaigns/single/${campaign.id}`
        );
        message.success({
          content: 'Copied link to clipboard',
          className: 'message-dark-modal',
          icon: <SuccessSvg className="anticon" />
        });
      }
    },
    {
      key: 'new-share-link',
      label: 'New share link',
      disabled: !campaign.permissions.createReviews || !campaign.assetsCount,
      icon: <LinkPlusSvg />,
      onClick: () => {
        if (!campaign.permissions.createReviews) return;
        dispatch(
          toggleShareAssetModal({
            visible: true,
            campaignId: campaign.id,
            defaultName: campaign.name || '',
            isWholeCampaign: true
          })
        );
      }
    },
    {
      key: 'share_upload_form',
      label: 'Share upload form',
      icon: <SendSvg />
    },
    ...(campaign.permissions.delete
      ? [
          {
            type: 'divider' as const
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteSvg className="b-opacity-icon" />,
            onClick: () => {
              dispatch(
                toggleEntityDeletionModal({
                  entity: campaign,
                  page: 'campaign',
                  permanent: false,
                  visible: true,
                  ...(onDeleteCallback ? { func: onDeleteCallback } : {})
                })
              );
            }
          }
        ]
      : [])
  ];

  return (
    <Dropdown
      disabled={isDeleted && !campaign.permissions.delete}
      trigger={['click']}
      onOpenChange={(value) => setIsDropDownOpen(value)}
      open={isDropDownOpen}
      menu={{
        items: isDeleted ? deletedItems : activeItems,
        onClick: (e) => {
          e.domEvent.stopPropagation();
          setIsDropDownOpen(false);
        }
      }}
      overlayClassName={classNames(
        'entities-dropdown-menu-overlay',
        overlayClassName
      )}
      placement={placement}
    >
      <DropdownMenuButton
        onOpenChange={setIsDropDownOpen}
        className={triggerClassName}
      />
    </Dropdown>
  );
}

export default CampaignDropDownMenu;
